import { msg } from "@lingui/core/macro";
import { useCallback, useEffect } from "react";
import { generatePath, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import type { Person } from "@/types/api";
import type { BookingState } from "@/types/booking";
import { getTotalGuests } from "@/utils/booking";

export const STEPS = {
  dates: {
    title: msg`When do you want to go camping?`,
    flow: "search",
    nextButtonLabel: msg`Next`,
  },
  accommodation: {
    title: msg`What would you like to camp with?`,
    flow: "search",
    nextButtonLabel: msg`Next`,
  },
  guests: {
    title: msg`How many guests?`,
    flow: "search",
    nextButtonLabel: msg`Search`,
  },
  results: {
    title: msg`Accommodation`,
    flow: "search",
    nextButtonLabel: "",
  },
  extras: {
    title: msg`Extras`,
    flow: "booking",
    nextButtonLabel: msg`Next`,
  },
  details: {
    title: msg`Details`,
    flow: "booking",
    nextButtonLabel: msg`Proceed to Payment`,
  },
  payment: {
    title: msg`Payment`,
    flow: "booking",
    nextButtonLabel: msg`Confirm and pay`,
  },
  "payment-stripe": {
    title: msg`Stripe Payment`,
    flow: "booking",
    nextButtonLabel: msg`Confirm and pay`,
  },
  confirmation: {
    title: msg`Booking Confirmation`,
    flow: "booking",
    nextButtonLabel: "",
  },
} as const;

function checkStepAvailability(
  step: keyof typeof STEPS,
  state: BookingState,
  persons: Person[],
): boolean {
  switch (step) {
    case "dates":
      return true;
    case "accommodation":
      return !!(state.dateFrom && state.dateUntil);
    case "guests":
      return checkStepAvailability("accommodation", state, persons) && !!state.selectedCampsiteType;
    case "results":
      return (
        checkStepAvailability("guests", state, persons) &&
        getTotalGuests(state.selectedPersons, persons, "adult") > 0
      );
    case "extras":
      return state.selectedCampsite !== null;
    case "details":
      return checkStepAvailability("extras", state, persons);
    case "payment":
      return checkStepAvailability("details", state, persons) && state.detailsValid;
    case "payment-stripe":
      return !!state.bookingId;
    case "confirmation":
      return true;
    default:
      return false;
  }
}

export function useStepNavigation() {
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, campground } = useParams();
  const [searchParams] = useSearchParams();

  const getSteps = useCallback(() => {
    const searchSteps = ["dates", "accommodation", "guests", "results"];
    let bookingSteps = ["extras", "details", "payment", "confirmation"];
    if (state.price?.payment_system === "ONLINE") {
      bookingSteps = ["extras", "details", "payment", "payment-stripe", "confirmation"];
    }
    return [...searchSteps, ...bookingSteps];
  }, [state.price?.payment_system]);

  const getCurrentStepIndex = () => {
    return getSteps().indexOf(currentStep);
  };

  // Path: /:language/:campground/:flow/:step/[:param]
  // Index:    1          2         3     4      5
  const currentStep: keyof typeof STEPS = location.pathname.split("/")[4] as keyof typeof STEPS;

  const currentStepIndex = getCurrentStepIndex();
  const nextStep: keyof typeof STEPS = getSteps()[currentStepIndex + 1] as keyof typeof STEPS;
  const previousStep: keyof typeof STEPS = getSteps()[currentStepIndex - 1] as keyof typeof STEPS;
  const currentFlow = STEPS[currentStep].flow;

  const isStepAvailable = useCallback(
    (step: keyof typeof STEPS) =>
      checkStepAvailability(step, state, campgroundData.campground.persons),
    [state, campgroundData.campground.persons],
  );

  const goToStep = useCallback(
    (target: keyof typeof STEPS) => {
      const flow = STEPS[target].flow;
      const path = generatePath(`/:language/:campground/:flow/:step`, {
        language: language!,
        campground: campground!,
        flow: flow,
        step: target,
      });
      const search = searchParams.toString();
      navigate(search ? `${path}?${search}` : path);
    },
    [language, campground, navigate, searchParams],
  );

  const goToNextStep = useCallback(() => {
    goToStep(nextStep);
  }, [nextStep, goToStep]);

  const goToPreviousStep = useCallback(() => {
    goToStep(previousStep);
  }, [previousStep, goToStep]);

  useEffect(() => {
    if (!isStepAvailable(currentStep)) {
      goToStep("dates");
    }
  }, [currentStep, isStepAvailable, goToStep]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return {
    currentStep,
    currentFlow,
    isStepAvailable,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    getSteps,
    nextStep,
    previousStep,
  };
}
